<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">
    <div class="grid md:grid-cols-2 gap-6">
      <!-- Left Column -->
      <div class="space-y-6">
        <!-- Name -->
        <TextInput
          v-model="fields.name.value"
          label="Name *"
          :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true"
        />

        <!-- Email -->
        <TextInput
          v-model="fields.email.value"
          type="email"
          label="Email *"
          :error="fields.email.touched ? fields.email.error : null"
          @blur="fields.email.touched = true"
        />

        <!-- Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Password *
          </label>
          <div class="relative">
            <input
              v-model="fields.password.value"
              :type="showPassword ? 'text' : 'password'"
              class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
              :class="{ 'border-red-300': fields.password.touched && fields.password.error }"
              @blur="fields.password.touched = true"
            />
            <button
              type="button"
              tabindex="-1"
              @click="showPassword = !showPassword"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {{ showPassword ? '👁️' : '👁️‍🗨️' }}
            </button>
          </div>
          <p v-if="fields.password.touched && fields.password.error" class="mt-1 text-sm text-red-600">
            {{ fields.password.error }}
          </p>
        </div>

        <!-- Confirm Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Confirm Password *
          </label>
          <div class="relative">
            <input
              v-model="fields.confirm_password.value"
              :type="showConfirmPassword ? 'text' : 'password'"
              class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
              :class="{ 'border-red-300': fields.confirm_password.touched && fields.confirm_password.error }"
              @blur="fields.confirm_password.touched = true"
            />
            <button
              type="button"
              tabindex="-1"
              @click="showConfirmPassword = !showConfirmPassword"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {{ showConfirmPassword ? '👁️' : '👁️‍🗨️' }}
            </button>
          </div>
          <p v-if="fields.confirm_password.touched && fields.confirm_password.error" class="mt-1 text-sm text-red-600">
            {{ fields.confirm_password.error }}
          </p>
        </div>

        <!-- Mobile Number -->
        <PhoneInput
          v-model="fields.mobile_number.value"
          v-model:countryCode="fields.country_code.value"
          label="Mobile Number *"
          :error="fields.mobile_number.touched ? fields.mobile_number.error : null"
          @blur="fields.mobile_number.touched = true"
        />
      </div>

      <!-- Right Column -->
      <div class="space-y-6">
        <!-- Profile Photo -->
        <div class="max-w-[320px]">
          <FileUpload
            v-model="fields.profile_photo.value"
            accept="image/*"
            path="admin/user/profile"
            :max-size="5"
            label="Profile Photo"
            :multiple="false"
            :error="fields.profile_photo.touched ? fields.profile_photo.error : null"
          @blur="fields.profile_photo.touched = true"
          />
        </div>

        <!-- Cover Photo -->
        <div class="max-w-[320px]">
          <FileUpload
            v-model="fields.cover_photo.value"
            accept="image/*"
            path="admin/user/cover"
            :max-size="5"
            label="Cover Photo"
            :multiple="false"
            :error="fields.cover_photo.touched ? fields.cover_photo.error : null"
          @blur="fields.cover_photo.touched = true"
          />
        </div>
      </div>
    </div>

    <!-- Profile Text Editor -->
    <div class="space-y-6">
      <div>
        
      </div>

      <!-- Submit Button -->
      <div class="flex justify-end">
        <button
          type="submit"
          class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Next Step
        </button>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useFormValidation } from '../../../composables/useFormValidation';
import { email, required } from '../../../utils/validation';
import TextInput from '../../ui/form/TextInput.vue';
import PhoneInput from '../../ui/form/PhoneInput.vue';
import FileUpload from '../../ui/form/FileUpload.vue';

const props = defineProps<{
  data?: Record<string, any>
}>()

const emit = defineEmits<{
  (e: 'complete', data: any): void
}>()

const showPassword = ref(false)
const showConfirmPassword = ref(false)

// Form validation setup
const { fields, formData, setFieldRules, validateForm } = useFormValidation({
  profile_photo: props.data?.profile_photo || null,
  name: props.data?.name || '',
  email: props.data?.email || '',
  password: props.data?.password || '',
  confirm_password: props.data?.confirm_password || '',
  mobile_number: props.data?.mobile_number || '',
  country_code: props.data?.country_code || 'US',
  cover_photo: props.data?.cover_photo || null,
  profile_text: props.data?.profile_text || ''
})

// Set validation rules
setFieldRules('name', [required('Name is required')])
setFieldRules('email', [required('Email is required'), email()])
setFieldRules('password', [
  required('Password is required'),
  {
    validate: (value: string) => value.length >= 6,
    message: 'Password must be at least 6 characters'
  },
  {
    validate: (value: string) => /\d/.test(value) && /[a-zA-Z]/.test(value),
    message: 'Password must contain at least one letter and one number'
  }
])
setFieldRules('confirm_password', [
  required('Please confirm your password'),
  {
    validate: (value: string) => value === fields.value.password.value,
    message: 'Passwords do not match'
  }
])
setFieldRules('mobile_number', [required('Mobile number is required')])


const handleSubmit = () => {
  if (validateForm()) {
    emit('complete', formData.value)
  }
}

</script>
